"use client";

import { submitFeedback } from "@/actions/feedback";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedbackPrompt } from "@/contexts/FeedbackPromptContext";
import { FeedbackRating } from "@/types";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
  addToast,
  useDisclosure,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState, useTransition } from "react";

// Rating options with material-symbols icons
const RATING_OPTIONS: {
  value: FeedbackRating;
  icon: string;
  labelKey: string;
  color: string;
}[] = [
  {
    value: "awful",
    icon: "material-symbols:sentiment-very-dissatisfied",
    labelKey: "feedback.ratings.awful",
    color: "text-red-500",
  },
  {
    value: "bad",
    icon: "material-symbols:sentiment-dissatisfied",
    labelKey: "feedback.ratings.bad",
    color: "text-orange-500",
  },
  {
    value: "neutral",
    icon: "material-symbols:sentiment-neutral",
    labelKey: "feedback.ratings.neutral",
    color: "text-yellow-500",
  },
  {
    value: "good",
    icon: "material-symbols:sentiment-satisfied",
    labelKey: "feedback.ratings.good",
    color: "text-lime-500",
  },
  {
    value: "excellent",
    icon: "material-symbols:sentiment-very-satisfied",
    labelKey: "feedback.ratings.excellent",
    color: "text-green-500",
  },
];

export function FeedbackButton() {
  const t = useTranslations();
  const { user } = useAuth();
  const { triggerSource, markFeedbackShown } = useFeedbackPrompt();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [comments, setComments] = useState("");
  const [isPending, startTransition] = useTransition();
  const [justSubmitted, setJustSubmitted] = useState(false);
  const pathname = usePathname();
  const isChatRoute =
    pathname?.includes("/chat") || pathname?.includes("/keskustelu");

  // Auto-cooldown gating: skip auto-popup if user just submitted (this
  // session) or already submitted within the last 24h.
  const canAutoOpen = useMemo(() => {
    if (!user || justSubmitted) return false;
    if (!user.lastFeedbackSentAt) return true;
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
    return user.lastFeedbackSentAt <= twentyFourHoursAgo;
  }, [user, justSubmitted]);

  useEffect(() => {
    if (!triggerSource || isOpen || justSubmitted) return;
    if (triggerSource === "auto" && (!canAutoOpen || isChatRoute)) {
      // Auto path waits out cooldown/chat routes. The flag stays set so the
      // modal can still open once the user navigates away or the cooldown
      // lapses. Manual path bypasses both checks — server enforces flood.
      return;
    }
    onOpen();
  }, [triggerSource, isOpen, onOpen, canAutoOpen, isChatRoute, justSubmitted]);

  // Render the modal only for signed-in users — anon users can't submit.
  if (!user) {
    return null;
  }

  const handleSubmit = () => {
    if (!rating) {
      addToast({
        title: t("feedback.errors.selectRating"),
        color: "warning",
      });
      return;
    }

    startTransition(async () => {
      const result = await submitFeedback({
        comments,
        rating,
      });

      if (result.success) {
        addToast({
          title: t("feedback.success"),
          color: "success",
        });
        // Reset form, close modal, and hide button for this session
        setRating(null);
        setComments("");
        setJustSubmitted(true);
        onClose();
      } else {
        addToast({
          title: result.error || t("common.unknownError"),
          color: "danger",
        });
      }
    });
  };

  const handleClose = () => {
    // Reset form on close
    setRating(null);
    setComments("");
    // Mark feedback as shown (prevents auto-popup from showing again)
    markFeedbackShown();
    onClose();
  };

  return (
    <>
      {/* Feedback modal — trigger lives in the main menu (NavDrawer) */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isDismissable={false}
        backdrop="opaque"
        size="sm"
        scrollBehavior="inside"
        classNames={{
          base: "bg-[#0f1219] border border-amber-500/20",
          header: "border-b border-amber-500/10",
          body: "py-6",
          footer: "border-t border-amber-500/10",
        }}
      >
        <ModalContent>
          {/* Decorative top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500/60 to-transparent rounded-t-lg" />

          <ModalHeader className="flex flex-col gap-1 pt-5">
            <div className="flex items-center gap-3 justify-center">
              <div className="h-px w-8 bg-gradient-to-r from-transparent to-amber-500/40" />
              <h2 className="text-xl font-cinzel font-bold text-amber-400 tracking-wide">
                {t("feedback.title")}
              </h2>
              <div className="h-px w-8 bg-gradient-to-l from-transparent to-amber-500/40" />
            </div>
            <p className="text-sm text-white/60 font-spectral text-center mt-1">
              {t("feedback.description")}
            </p>
          </ModalHeader>
          <ModalBody className="py-6">
            {/* Rating selector */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
                  {t("feedback.ratingLabel")}
                </label>
                <div className="flex-1 h-px bg-amber-500/20" />
              </div>
              <div className="flex justify-center">
                <div className="grid grid-cols-5 gap-1 sm:gap-2 rounded-xl bg-[#0a0c10]/60 border border-amber-500/10 p-2 sm:p-3 w-full max-w-sm">
                  {RATING_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRating(option.value)}
                      className={`
                        flex flex-col items-center justify-center p-1.5 sm:p-3 rounded-lg
                        transition-all duration-200 min-w-0 w-full aspect-square
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500
                        ${
                          rating === option.value
                            ? "bg-amber-500/20 ring-2 ring-amber-500 scale-105 sm:scale-110"
                            : "bg-white/5 hover:bg-white/10 border border-white/10"
                        }
                      `}
                      aria-label={t(option.labelKey)}
                      aria-pressed={rating === option.value}
                    >
                      <Icon
                        icon={option.icon}
                        className={`text-xl sm:text-3xl ${option.color}`}
                      />
                      <span
                        className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 leading-tight text-center ${
                          rating === option.value
                            ? "text-amber-400"
                            : "text-white/50"
                        }`}
                      >
                        {t(option.labelKey)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Comments textarea */}
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-medium text-amber-500/80 uppercase tracking-wider">
                  {t("feedback.commentsLabel")}
                </label>
                <div className="flex-1 h-px bg-amber-500/20" />
              </div>
              <Textarea
                placeholder={t("feedback.commentsPlaceholder")}
                value={comments}
                onValueChange={setComments}
                minRows={3}
                maxRows={6}
                classNames={{
                  inputWrapper:
                    "bg-[#0a0c10]/60 border border-amber-500/10 hover:border-amber-500/30 focus-within:border-amber-500/50",
                  input: "text-white/80 placeholder:text-white/30",
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="flat"
              onPress={handleClose}
              isDisabled={isPending}
              className="bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onPress={handleSubmit}
              isLoading={isPending}
              isDisabled={!rating}
              className="bg-gradient-to-r from-amber-600 to-amber-500 text-white font-medium hover:from-amber-500 hover:to-amber-400 disabled:opacity-50"
            >
              {t("common.submit")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
